import {AfterViewInit, Component, ElementRef, EventEmitter, forwardRef, Inject, Input, OnChanges, Output, Renderer2, SimpleChanges, Type, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {DOCUMENT} from '@angular/platform-browser';
import {UploaderService} from 'app/shared/components/uploader/uploader.service';
import {ImageFormat, PresetValues} from './html.editor-ext';
import {ImageResize} from 'quill-image-resize-module';
import {ImagePreEmbedModalComponent} from './image-pre-embed-modal/image-pre-embed-modal.component';
import {RxComponent} from 'app/core/components/common/rx.component';
import {SmartModalService} from '../../../../core/services/smart-modal.service';

export interface CustomOption {
  import: string;
  whitelist: Array<any>;
}

declare var Quill: any;
declare var $: any;
const Clipboard = Quill.import('modules/clipboard');
const Delta = Quill.import('delta');
const Parchment = Quill.import('parchment')
var Block = Quill.import('blots/block');
@Component({
  selector: 'itpx-html-editor',
  templateUrl: './html-editor.component.html',
  styleUrls: ['./html-editor.component.css'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => HtmlEditorComponent),
    multi: true
  }]
})
export class HtmlEditorComponent extends RxComponent implements AfterViewInit, ControlValueAccessor, OnChanges {

  itpxEditor: any;
  editorElem: HTMLElement;
  emptyArray: any[] = [];
  content: any;
  defaultModules = {
    toolbar: [
      { 'header': [1, 2, 3, 4, 5, 6, false] },
      { 'list': 'ordered' }, { 'list': 'bullet' }
    ]

  };
  defaultStyle: any = { height: '200px' };

  @Input() theme: string;
  @Input() modules: { [index: string]: Object };
  @Input() readOnly: boolean;
  @Input() placeholder: string;
  @Input() formats: string[];
  @Input() style: any;
  @Input() strict: boolean = true;
  @Input() scrollingContainer: HTMLElement | string;
  @Input() bounds: HTMLElement | string;
  @Input() customOptions: CustomOption[] = [];
  @Input() bucket: string;
  @Input() limit: number;
  @Input() disabled: boolean;
  @Input() isPlainClipboard: boolean;
  @Input() initFunctions: any;
  @Input() inlineHtml: any;
  @Output() onEditorCreated: EventEmitter<any> = new EventEmitter();
  @Output() onContentChanged: EventEmitter<any> = new EventEmitter();
  @Output() onSelectionChanged: EventEmitter<any> = new EventEmitter();
  @Output() editorLengthChanged: EventEmitter<any> = new EventEmitter();

  @ViewChild('container') container: ElementRef;

  onModelChange: Function = () => { };
  onModelTouched: Function = () => { };

  constructor(
    private elementRef: ElementRef,
    @Inject(DOCUMENT) private doc: any,
    private renderer: Renderer2,
    private uploaderService: UploaderService,
    private modalService: SmartModalService
  ) {
    super()
  }

  ngAfterViewInit() {
    const toolbarElem = this.elementRef.nativeElement.querySelector('[itpx-editor-toolbar]');

    if (typeof this.modules === 'string') {
      this.modules = JSON.parse(this.modules);
    }
    let modules: any = this.modules || this.defaultModules;

    let placeholder = 'Insert text here ...';

    if (this.placeholder)
      placeholder = this.placeholder.trim();

    if (toolbarElem)
      modules['toolbar'] = toolbarElem;

    this.elementRef.nativeElement.insertAdjacentHTML('beforeend', '<div itpx-editor-element></div>');
    this.editorElem = this.elementRef.nativeElement.querySelector('[itpx-editor-element]');

    if (typeof this.style === 'string') {
      this.style = JSON.parse(this.style);
    }

    let style: any = this.style || this.defaultStyle;

    Object.keys(style).forEach((key: string) => {
      this.renderer.setStyle(this.editorElem, key, style[key]);
    });
    Block.tagName = 'div';
    Quill.register(Block);

    // modules.toolbar.push(['link'])
    // modules['clipboard'] = {
    //   matchVisual: false
    // };
    // modules['formats'] = [
    //   'bold',
    //   'header',
    //   'italic',
    //   'link',
    //   'list',
    //   'blockquote',
    //   'image',
    //   'indent'
    // ];

    this.itpxEditor = new Quill(this.editorElem, {
      modules: modules,
      placeholder: placeholder,
      readOnly: this.readOnly || false,
      theme: this.theme || 'snow',
      formats: this.formats,
      bounds: this.bounds || this.doc.body,
      strict: this.strict,
      scrollingContainer: this.scrollingContainer
    });

    this.itpxEditor.enable(!this.disabled);
    // ----------------------------
    if (this.initFunctions) {
      Object.keys(this.initFunctions).forEach(key => this.initFunctions[key]())
    }
    // -------------------------
    if (this.content) {
      // const contents = this.itpxEditor.clipboard.convert(this.content);
      // this.itpxEditor.setContents(contents);
      this.itpxEditor.root.innerHTML = this.content
      this.itpxEditor.history.clear();
      this.editorLengthChanged.emit(this.itpxEditor.getLength());
    }

    this.onEditorCreated.emit(this.itpxEditor);

    this.itpxEditor.on('selection-change', (range: any, oldRange: any, source: string) => {
      this.onSelectionChanged.emit({
        editor: this.itpxEditor,
        range: range,
        oldRange: oldRange,
        source: source
      });

      if (!range) {
        this.onModelTouched();
      }
    });
    // this.renderer.addClass(this.editorElem.children[0], 'editor');
    if (this.isPlainClipboard) {
      this.itpxEditor.clipboard.addMatcher(Node.ELEMENT_NODE, function (node, delta) {
        let plaintext = $(node).text();
        return new Delta().insert(plaintext);
      });
    }

    this.itpxEditor.on('text-change', (delta: any, oldDelta: any, source: string) => {
      if (this.limit && this.itpxEditor.getLength() > this.limit) {
        this.itpxEditor.deleteText(this.limit, this.itpxEditor.getLength());
      }
      let html: (string | null) = this.editorElem.children[0].innerHTML;
      const text = this.itpxEditor.getText();

      if (html === '<div><br></div>') {
        html = null;
      }
      this.editorLengthChanged.emit(this.itpxEditor.getLength());

      this.onModelChange(html);

      this.onContentChanged.emit({
        editor: this.itpxEditor,
        html: html,
        text: text,
        delta: delta,
        oldDelta: oldDelta,
        source: source
      });
    });

    if (this.modules && this.modules.ImageResize) Quill.register('imageResize', ImageResize);
    if (this.uploadImageToS3) this.registerImageHandlerComponents()
  }

  registerImageHandlerComponents() {
    Quill.register(ImageFormat, true);
    this.itpxEditor.getModule('toolbar').addHandler('image', () => {
      this.selectLocalImage();
    });
  }

  selectLocalImage() {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.click();

    // Listen upload local image and save to server
    input.onchange = () => {
      const file = input.files[0];

      // file type is only image.
      if (/^image\//.test(file.type)) {
        this.uploadImageToS3(file);
      } else {
        console.warn('You could only upload images.');
      }
    };
  }

  uploadImageToS3(file: File) {
    this.uploaderService.uploadContent(file, file.name, this.bucket)
      .subscribe(
        data => {
          if (data.completed) this.insertToEditor(data.url.split('?uploadId=')[0])
        }
      )
  }

  insertToEditor(url: string) {
    const range = this.itpxEditor.getSelection();
    this.modalService.open(<Type<Component>>ImagePreEmbedModalComponent, {}, 'Pre-Embed Image')
      .neverError()
      .bindToLifecycle(this)
      .subscribe(
        data => {
          PresetValues['alt'] = data.result['alt']
          this.itpxEditor.insertEmbed(range.index, 'image', url);
        }
      )
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['readOnly'] && this.itpxEditor) {
      this.itpxEditor.enable(!changes['readOnly'].currentValue);
    }
  }

  writeValue(currentValue: any) {
    this.content = currentValue;

    if (this.itpxEditor) {
      if (currentValue) {
        this.itpxEditor.setContents(this.itpxEditor.clipboard.convert(this.content));
        return;
      }
      this.itpxEditor.setText('');
    }
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/controls/html-editor/html-editor.component.ts